import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { chartOptions } from '@/features/charts/api/charts.options';
import {
  buildChartLineSeries,
  buildChartRangeSeries
} from '@/features/charts/lib/buildChartLineSeries';
import { findSeriesBasedOnKey } from '@/features/charts/lib/findSeriesBasedOnKey';
import { getFieldDecimalPlaces } from '@/features/charts/lib/getFieldDecimalPlaces';
import { getSeriesId } from '@/features/charts/lib/getSeriesId';
import { getSeriesName } from '@/features/charts/lib/getSeriesName';
import { getShortTitle } from '@/features/charts/lib/getShortTitle';
import { parseSeasonalSeriesDataToSeries } from '@/features/charts/lib/parseSeasonalSeriesDataToSeries';
import type {
  ChartDataRange,
  KeyData,
  SeriesData,
  SeriesMetadata
} from '@/features/charts/types/chartData';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import getDecimalPlaces from '@/shared/lib/format/getDecimalPlaces';
import type { TemplateChartSeries } from '@/shared/types/charts';

interface ParseDataSeriesParams {
  symbolIndex: number;
  series: SeriesData;
  fieldIndex: number;
  metadata: SeriesMetadata;
  field: string;
  shortTitle: string | undefined;
  key: KeyData;
}

function parseDataSeries({
  symbolIndex,
  series,
  fieldIndex,
  metadata,
  field,
  shortTitle,
  key
}: ParseDataSeriesParams) {
  const combinedIndex = symbolIndex * Object.keys(series).length + fieldIndex;
  const decimalPlaces = metadata.Increment
    ? getDecimalPlaces(metadata.Increment)
    : getFieldDecimalPlaces(series[field]);
  const name = getSeriesName(shortTitle, metadata.Title);
  const id = getSeriesId(key, field);
  const dataRange = Object.keys(series)
    .map(Number)
    .filter(yearKey => yearKey && !Number.isNaN(yearKey));

  const min = Math.min(...dataRange);
  const max = Math.max(...dataRange) - 1;
  const context = {
    ...key,
    ...metadata,
    decimalPlaces,
    combinedIndex,
    field
  };
  const timeRef = key.TimeRef;
  const timezone = metadata.TimeZone;

  const timeSeriesPoints = parseSeasonalSeriesDataToSeries({
    series,
    field,
    decimalPlaces,
    timeRef,
    timezone
  });

  if (!timeSeriesPoints) return null;

  if (field === 'range') {
    if (min >= max) return null;
    return buildChartRangeSeries({
      id: `${min}-${max} ${id} `,
      name: `${min}-${max} ${name} `,
      timeSeriesPoints,
      context
    });
  } else if (field === 'average') {
    if (min >= max) return null;
    return buildChartLineSeries({
      id: `Avg ${min}-${max} ${id}`,
      name: `Avg ${min}-${max} ${name}`,
      timeSeriesPoints,
      context
    });
  }
  return buildChartLineSeries({
    id: (field ? `${field} ` : '') + id,
    name: (field ? `${field} ` : '') + name,
    timeSeriesPoints,
    context,
    visible: fieldIndex > Object.keys(series).length - 5
  });
}

const useSeasonalChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  chartDataRange: ChartDataRange,
  isInViewport = true
) => {
  const {
    data: seasonalChartData,
    isError: isSeasonalChartDataError,
    isLoading: isSeasonalChartDataLoading
  } = useQuery({
    ...chartOptions.seasonal(templateChartSeries, chartDataRange),
    enabled: templateChartSeries !== undefined && !!chartDataRange.from && isInViewport
  });

  const parseSeasonalDataToChartSeries = useCallback(
    ({
      series,
      symbolIndex,
      key,
      metadata
    }: {
      series: SeriesData;
      symbolIndex: number;
      key: KeyData;
      metadata: SeriesMetadata;
    }) => {
      const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
      const shortTitle = getShortTitle(templateSeries);
      return Object.keys(series)
        .map((field: string, fieldIndex) => {
          return parseDataSeries({
            symbolIndex,
            series,
            fieldIndex,
            metadata,
            field,
            shortTitle,
            key
          });
        })
        .filter((s): s is ExtendedSeriesOptionsType => s !== null);
    },
    [templateChartSeries]
  );
  const chartSeries = useMemo(() => {
    if (seasonalChartData?.symbols)
      return seasonalChartData.symbols.flatMap(({ key, series, metadata }, symbolIndex) =>
        parseSeasonalDataToChartSeries({ series, symbolIndex, key, metadata })
      );
  }, [seasonalChartData, parseSeasonalDataToChartSeries]);

  return {
    loading:
      isSeasonalChartDataLoading || !templateChartSeries || (!isInViewport && !seasonalChartData),
    error: isSeasonalChartDataError,
    chartSeries
  };
};

export default useSeasonalChartSeries;
